import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Loader2,
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  Mountain,
  Shield,
  Globe,
} from "lucide-react";
import { useLang } from "@/i18n";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConfirmationPage() {
  const { bookingId: paramBookingId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const navigate = useNavigate();
  const { t, toggleLang } = useLang();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const pollPaymentStatus = useCallback(async (sid, attempt) => {
    if (attempt >= 8) {
      setPaymentStatus("timeout");
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/checkout/status/${sid}`);
      const data = res.data;

      if (data.payment_status === "paid") {
        setPaymentStatus("paid");
        if (data.booking_id) {
          const bookingRes = await axios.get(`${API}/bookings/${data.booking_id}`);
          setBooking(bookingRes.data);
        }
        setLoading(false);
      } else if (data.status === "expired") {
        setPaymentStatus("expired");
        setLoading(false);
      } else {
        setPaymentStatus("processing");
        setPollCount(attempt + 1);
        setTimeout(() => pollPaymentStatus(sid, attempt + 1), 2500);
      }
    } catch {
      if (attempt < 5) {
        setTimeout(() => pollPaymentStatus(sid, attempt + 1), 3000);
      } else {
        setPaymentStatus("error");
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else if (paramBookingId) {
      axios
        .get(`${API}/bookings/${paramBookingId}`)
        .then((res) => setBooking(res.data))
        .catch(() => navigate("/"))
        .finally(() => setLoading(false));
    } else {
      navigate("/");
    }
  }, [sessionId, paramBookingId, navigate, pollPaymentStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1a0f] flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-10 h-10 text-[#2d7a4a] animate-spin" data-testid="loading-spinner" />
        <p className="text-[#8faa8f] text-sm">
          {paymentStatus === "processing"
            ? `${t("verifying")} (${pollCount}/8)`
            : t("loading")}
        </p>
      </div>
    );
  }

  if (paymentStatus === "timeout" || paymentStatus === "error") {
    return (
      <div className="min-h-screen bg-[#0a1a0f] text-[#e8f0e8] flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 glass-card rounded-xl flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-[#d4a056]" />
          </div>
          <h1 className="text-3xl font-bold uppercase mb-2" data-testid="payment-error-title">
            {t("verifyTitle")}
          </h1>
          <p className="text-[#8faa8f] mb-8">{t("verifyDesc")}</p>
          <button
            data-testid="back-home-btn"
            onClick={() => navigate("/")}
            className="border-2 border-[#2d7a4a] text-[#2d7a4a] font-bold uppercase tracking-wider px-8 py-3 hover:bg-[#2d7a4a] hover:text-white transition-all text-sm rounded-lg"
          >
            {t("backHome")}
          </button>
        </div>
      </div>
    );
  }

  if (!booking) return null;

  const tourNameKey =
    booking.tour_type === "single"
      ? "tourSingleName"
      : booking.tour_type === "double"
        ? "tourDoubleName"
        : "tourMottaroneName";

  return (
    <div className="min-h-screen bg-[#0a1a0f] text-[#e8f0e8] flex flex-col items-center justify-center px-4">
      {/* Language Toggle */}
      <button
        data-testid="lang-toggle-confirm"
        onClick={toggleLang}
        className="fixed top-4 right-4 z-50 flex items-center gap-1 px-2 py-1 rounded-full glass-card text-xs text-[#d4a056]"
      >
        <Globe className="w-3 h-3" /> {t("langToggle")}
      </button>

      <div className="max-w-md w-full text-center" data-testid="confirmation-container">
        <div className="w-20 h-20 bg-[#2d7a4a] rounded-xl flex items-center justify-center mx-auto mb-6 animate-slide-up shadow-lg shadow-[#2d7a4a]/20">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>

        <h1
          className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-2 animate-slide-up-delay-1"
          data-testid="confirmation-title"
        >
          {t("confirmed")}
        </h1>
        <p className="text-[#8faa8f] mb-8 animate-slide-up-delay-1">{t("confirmSubtitle")}</p>

        <div
          className="glass-card rounded-xl p-6 text-left animate-slide-up-delay-2"
          data-testid="booking-details"
        >
          <div className="flex items-center gap-2 mb-4">
            <Mountain className="w-4 h-4 text-[#2d7a4a]" />
            <h3 className="font-bold uppercase text-sm tracking-widest text-[#2d7a4a]">
              {t(tourNameKey)}
            </h3>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3 text-[#8faa8f]">
              <Calendar className="w-4 h-4 text-[#2d7a4a] flex-shrink-0" />
              <span>{booking.booking_date}</span>
            </div>
            <div className="flex items-center gap-3 text-[#8faa8f]">
              <Clock className="w-4 h-4 text-[#2d7a4a] flex-shrink-0" />
              <span>{booking.time_slot}</span>
            </div>
            <div className="flex items-center gap-3 text-[#8faa8f]">
              <User className="w-4 h-4 text-[#2d7a4a] flex-shrink-0" />
              <span>{booking.customer_name}</span>
            </div>
            <div className="flex items-center gap-3 text-[#8faa8f]">
              <Mail className="w-4 h-4 text-[#2d7a4a] flex-shrink-0" />
              <span>{booking.customer_email}</span>
            </div>
            <div className="flex items-center gap-3 text-[#8faa8f]">
              <Phone className="w-4 h-4 text-[#2d7a4a] flex-shrink-0" />
              <span>{booking.customer_phone}</span>
            </div>
            <div className="flex items-center gap-3 text-[#8faa8f]">
              <Shield className="w-4 h-4 text-[#2d7a4a] flex-shrink-0" />
              <span>{t("licenseB")}: {booking.license_b_confirmed ? t("yes") : t("no")}</span>
            </div>

            <div className="border-t border-[#1a4a2e] pt-3 mt-3 flex justify-between text-lg font-bold">
              <span className="text-[#e8f0e8]">{t("total")}</span>
              <span className="text-[#d4a056] text-glow-gold">€{booking.total_price}</span>
            </div>
          </div>

          <p className="text-xs text-[#3a5a3a] mt-4 font-mono">
            ID: {booking.id}
          </p>
        </div>

        <button
          data-testid="back-home-btn"
          onClick={() => navigate("/")}
          className="mt-8 border-2 border-[#2d7a4a] text-[#2d7a4a] font-bold uppercase tracking-wider px-8 py-3 hover:bg-[#2d7a4a] hover:text-white transition-all text-sm animate-slide-up-delay-3 rounded-lg"
        >
          <ArrowLeft className="w-4 h-4 inline-block mr-2" />
          {t("backHome")}
        </button>
      </div>
    </div>
  );
}
